import { useCallback, useMemo } from 'react';
import { STATUS_CONFIG, MENU_LABELS } from '../../utils';
import type { TUseVehicleCardConfig, TUseVehicleCardReturn } from './vehicle-card.types';
import type { TActionId, TMenuActionId } from '../../utils';

const formatCurrency = (value: number): string => {
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format(value);
};

const formatKilometers = (km: number): string => {
	return new Intl.NumberFormat('pt-BR').format(km) + ' km';
};

const useVehicleCard = ({
	ad,
	onAction,
	onMenuAction
}: TUseVehicleCardConfig): TUseVehicleCardReturn => {
	const formattedPrice = useMemo(() => formatCurrency(ad.salePrice), [ad.salePrice]);

	const formattedKm = useMemo(() => formatKilometers(ad.kilometers), [ad.kilometers]);

	const yearDisplay = useMemo(
		() => `${ad.manufacturingYear}/${ad.modelYear} • ${formattedKm}`,
		[ad.manufacturingYear, ad.modelYear, formattedKm]
	);

	const statusConfig = useMemo(() => {
		const config = STATUS_CONFIG[ad.displayStatus];
		return {
			label: config.label,
			bgColor: config.bgColor,
			dotColor: config.dotColor
		};
	}, [ad.displayStatus]);

	const showMetrics = useMemo(() => {
		const config = STATUS_CONFIG[ad.displayStatus];
		return config.showMetrics;
	}, [ad.displayStatus]);

	const availableActions = useMemo(() => {
		const config = STATUS_CONFIG[ad.displayStatus];
		const actions = [...config.actions] as TActionId[];
		if (ad.upgradeEligible !== true) {
			return actions.filter((a) => a !== 'boost');
		}
		return actions;
	}, [ad.displayStatus, ad.upgradeEligible]);

	const menuOptions = useMemo(() => {
		const config = STATUS_CONFIG[ad.displayStatus];
		return (config.menuActions as ReadonlyArray<TMenuActionId>).map((id) => ({
			id,
			label: MENU_LABELS[id]
		}));
	}, [ad.displayStatus]);

	const menuDisabled = useMemo(() => STATUS_CONFIG[ad.displayStatus].menuDisabled, [ad.displayStatus]);

	const handleAction = useCallback(
		(action: TActionId) => {
			onAction?.(ad.id, action);
		},
		[ad.id, onAction]
	);

	const handleMenuAction = useCallback(
		(action: TMenuActionId) => {
			onMenuAction?.(ad.id, action);
		},
		[ad.id, onMenuAction]
	);

	return {
		formattedPrice,
		formattedKm,
		yearDisplay,
		showMetrics,
		statusConfig,
		availableActions,
		menuOptions,
		menuDisabled,
		handleAction,
		handleMenuAction
	};
};

export { useVehicleCard };
